import type {
  GltfCompatibilityIssue,
  GltfManifest,
  GltfTextureInfo,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'

const SUPPORTED_ATTRIBUTES = new Set([
  'POSITION',
  'NORMAL',
  'TANGENT',
  'TEXCOORD_0',
  'TEXCOORD_1',
  'COLOR_0',
])
const SUPPORTED_EXTENSIONS = new Set(['KHR_materials_unlit'])

function addIssue(
  issues: GltfCompatibilityIssue[],
  seen: Set<string>,
  issue: GltfCompatibilityIssue
): void {
  const key = `${issue.code}:${issue.feature}`
  if (seen.has(key)) return
  seen.add(key)
  issues.push(issue)
}

function scanTextureInfo(
  info: GltfTextureInfo | undefined,
  label: string,
  issues: GltfCompatibilityIssue[],
  seen: Set<string>
): void {
  for (const extension of Object.keys(info?.extensions ?? {})) {
    addIssue(issues, seen, {
      code: 'unsupported-material-extension',
      feature: `${label}:${extension}`,
    })
  }
}

/** 扫描首版低内存白名单无法完整保留的 glTF 语义。 */
export function scanGltfCompatibility(manifest: GltfManifest): readonly GltfCompatibilityIssue[] {
  const issues: GltfCompatibilityIssue[] = []
  const seen = new Set<string>()

  for (const extension of manifest.extensionsRequired ?? []) {
    if (SUPPORTED_EXTENSIONS.has(extension)) continue
    addIssue(issues, seen, {
      code: 'unsupported-extension',
      feature: extension,
    })
  }
  if (
    (manifest.skins?.length ?? 0) > 0 ||
    manifest.nodes?.some((node) => node.skin !== undefined)
  ) {
    addIssue(issues, seen, { code: 'unsupported-skin', feature: 'skin' })
  }

  manifest.accessors?.forEach((accessor, index) => {
    if (accessor.sparse) {
      addIssue(issues, seen, {
        code: 'unsupported-sparse-accessor',
        feature: `accessors[${index}].sparse`,
      })
    }
  })

  manifest.meshes?.forEach((mesh, meshIndex) => {
    if (mesh.weights) {
      addIssue(issues, seen, {
        code: 'unsupported-morph-target',
        feature: `meshes[${meshIndex}].weights`,
      })
    }
    mesh.primitives.forEach((primitive, primitiveIndex) => {
      for (const semantic of Object.keys(primitive.attributes)) {
        if (!SUPPORTED_ATTRIBUTES.has(semantic)) {
          addIssue(issues, seen, {
            code: 'unsupported-attribute',
            feature: `meshes[${meshIndex}].primitives[${primitiveIndex}].attributes.${semantic}`,
          })
        }
      }
      if (primitive.targets && primitive.targets.length > 0) {
        addIssue(issues, seen, {
          code: 'unsupported-morph-target',
          feature: `meshes[${meshIndex}].primitives[${primitiveIndex}].targets`,
        })
      }
      if ((primitive.mode ?? 4) !== 4) {
        addIssue(issues, seen, {
          code: 'unsupported-primitive-mode',
          feature: `primitive-mode-${primitive.mode}`,
        })
      }
      for (const extension of Object.keys(primitive.extensions ?? {})) {
        addIssue(issues, seen, {
          code: 'unsupported-extension',
          feature: extension,
        })
      }
    })
  })
  if (manifest.nodes?.some((node) => (node.weights?.length ?? 0) > 0)) {
    addIssue(issues, seen, { code: 'unsupported-morph-target', feature: 'node.weights' })
  }

  manifest.materials?.forEach((material, index) => {
    for (const extension of Object.keys(material.extensions ?? {})) {
      if (SUPPORTED_EXTENSIONS.has(extension)) continue
      addIssue(issues, seen, {
        code: 'unsupported-material-extension',
        feature: `materials[${index}]:${extension}`,
      })
    }
    scanTextureInfo(
      material.pbrMetallicRoughness?.baseColorTexture,
      `materials[${index}].baseColorTexture`,
      issues,
      seen
    )
    scanTextureInfo(
      material.pbrMetallicRoughness?.metallicRoughnessTexture,
      `materials[${index}].metallicRoughnessTexture`,
      issues,
      seen
    )
    scanTextureInfo(material.normalTexture, `materials[${index}].normalTexture`, issues, seen)
    scanTextureInfo(material.occlusionTexture, `materials[${index}].occlusionTexture`, issues, seen)
    scanTextureInfo(material.emissiveTexture, `materials[${index}].emissiveTexture`, issues, seen)
  })
  manifest.textures?.forEach((texture, index) => {
    for (const extension of Object.keys(texture.extensions ?? {})) {
      addIssue(issues, seen, {
        code: 'unsupported-material-extension',
        feature: `textures[${index}]:${extension}`,
      })
    }
  })

  return issues
}
